import React, { Component } from 'react';
import { Button, Container, Form } from 'react-bootstrap';

import ScreenBlockStatus from '../../../framework/route/ScreenBlockStatus';
import withNavigationBlocker from '../../../framework/route/WithNavigationBlocker';

import CountDownTimer from './CountDownTimer';
import CourseCompleted from './CourseCompleted';

import 'bootstrap/dist/css/bootstrap.min.css';

import '../../../framework/component/App.css';

import './Quiz.css';
import apiClient from "../../../framework/web/ApiClient";

interface ServerMessage {
    code: string;
    message: string;
}

interface QuestionOption {
    optionId: number;
    order: number;
    optionValue: string;
}

interface Question {
    questionId: number;
    question: string;
    type: string;
    score: number;
    options: QuestionOption[];
}

interface Quiz {
    quizId: number;
    name: string;
    timeLimit: number | null;
    questions: Question[];
    passingScore: number;
}

interface State {
    quiz: Quiz | null;
    loading: boolean;
    error: string | null;
    questionError: { [key: string]: boolean };
    answers: { [key: string]: string[] };
    score: number;
    complete: boolean;
    shouldBlock: ScreenBlockStatus;
    serverMessage: ServerMessage[] | null;
}

interface QuizProps {
    pathvar?: { id: string };
    navigate?: (path: string, state?: any) => void;
    shouldBlock: ScreenBlockStatus;
}

const PAGE_TITLE = "AMS - Test";

class Quiz extends Component<QuizProps, State> {

    constructor(props: QuizProps) {
        super(props);
        this.state = {
            quiz: null,
            loading: true,
            error: null,
            questionError: {},
            answers: {},
            score: 0,
            complete: false,
            shouldBlock: props.shouldBlock,
            serverMessage: null,
        };
    }

    componentDidMount() {
        document.title = PAGE_TITLE;

        let curQuizId = this.props.pathvar?.id;

        if (curQuizId) {
            this.fetchQuestions(curQuizId);
        }

        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
    };

    fetchQuestions = async (curQuizId: string) => {
        const headers = {
            'Access-Control-Allow-Origin': '*',
            "Content-Type": "application/json"
        };
        apiClient.get('/ams/api/v1/researcher-trainings/quizzes/' + curQuizId + '/questions', { withCredentials: true, headers })
            .then(response => {
                if (response.status != 204) {
                    if (response.data.errors) {
                        this.setState({ quiz: response.data.data, serverMessage: response.data.errors, loading: false, shouldBlock: this.setAndGetBlockStatus(false) })
                    } else {
                        this.setState({ quiz: response.data.data, loading: false });
                    }

                    const errors = response.data.data.questions?.reduce((acc: { [key: string]: boolean }, question: Question) => {
                        acc["q-" + question.questionId] = false;
                        return acc;
                    }, {} as { [key: string]: boolean });

                    this.setState({ questionError: errors });
                }
            }).catch(error => {
                this.setState({ error: 'Error fetching questions', loading: false });
            });
    };

    sendDataToServer = async (curQuizId: string) => {
        const response = await apiClient.post('/ams/api/v1/researcher-trainings/quizzes/' + curQuizId + '/submissions', { questionToAnswers: this.state.answers })
            .then(response => {
                if (response.status != 204) {
                    const data = response.data;
                    console.info("Response: " + JSON.stringify(data));
                    return data.data;
                }
            }).catch(error => {
                console.error('Failed to submit quiz data');
            });
        return response;
    };

    submitQuizAnswersToServer() {
        const { quiz, answers, questionError } = this.state;
        const errors = Object.keys(questionError).reduce((acc, key) => {
            questionError[key] = null == answers[key];
            if (true == questionError[key]) {
                acc[key] = true;
            }
            return acc;
        }, {} as { [key: string]: boolean });

        if (Object.keys(errors).length > 0) {
            return;
        }

        // Calculate score or any other logic
        //const score = Object.keys(this.state.answers).length;
        const score = 1;
        this.setState({ score }, async () => {
            if (this.props?.pathvar?.id) {
                let data = await this.sendDataToServer(this.props?.pathvar?.id);
                if (this.props.navigate && data) {
                    this.props.navigate('/quiz-result', { state: { passingScore: quiz?.passingScore, score: data['score'], pass: data['pass'], quizId: quiz?.quizId, quizName: quiz?.name } });
                }
            }
        });
    }

    setAndGetBlockStatus(block: boolean): ScreenBlockStatus {
        const { shouldBlock } = this.state;
        shouldBlock.status = block;

        if (!block) {
            window.removeEventListener('beforeunload', this.handleBeforeUnload);
        }

        return shouldBlock;
    }

    handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        this.setState({ shouldBlock: this.setAndGetBlockStatus(false) }, () => this.submitQuizAnswersToServer());
    };

    handleTimeout = () => {
        const { quiz } = this.state;
        this.setState({ shouldBlock: this.setAndGetBlockStatus(false) }, () => {
            if (this.props.navigate) {
                this.props.navigate('/timeout', { state: { timeLimit: quiz?.timeLimit, quizId: quiz?.quizId } });
            }
        });
    }

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target;
        const formGroupId = event.target.closest('.form-group')?.id;

        if (formGroupId) {
            const { quiz } = this.state;
            const newAnswers = { ...this.state.answers };
            const currentAnswers = newAnswers[formGroupId] || [];
            const question = quiz?.questions.filter(q => q.questionId === parseInt(formGroupId.split("-")[1]))[0];
            if ("MC_SINGLE_SELECT" === question?.type) {
                if (currentAnswers.includes(id)) {
                    delete newAnswers[formGroupId];
                } else {
                    newAnswers[formGroupId] = [id];
                }
            } else if ("MC_MULTI_SELECT" === question?.type) {
                if (currentAnswers.includes(id)) {
                    const index = currentAnswers.indexOf(id);
                    currentAnswers.splice(index, 1);
                    newAnswers[formGroupId] = currentAnswers;

                    if (currentAnswers.length == 0) {
                        delete newAnswers[formGroupId];
                    }
                } else {
                    newAnswers[formGroupId] = [...currentAnswers, id];
                }
            }
            this.setState({ answers: newAnswers });

            const allCompleted = quiz?.questions.length == Object.keys(newAnswers).length;
            if (allCompleted) {
                this.setState({ complete: true });
            } else {
                this.setState({ complete: false });
            }
        }
    };

    render() {
        const { quiz, loading, error, complete, serverMessage } = this.state;

        if (loading) {
            return <div>Loading...</div>;
        }

        if (error) {
            return <div>{error}</div>;
        }

        if (serverMessage && serverMessage[0] && "032001" === serverMessage[0].code) {
            return <CourseCompleted courseName={quiz?.name ?? ''} message={serverMessage[0].message} />
        }

        return (
            <Container fluid>
                <h2>{quiz?.name}</h2>
                <h6>All questions are mandatory. Please complete all questions to submit your test.</h6>
                <br />
                <CountDownTimer timeLeftInMin={quiz?.timeLimit} timeoutFunction={this.handleTimeout} />
                <Form onSubmit={this.handleSubmit}>
                    {quiz?.questions?.map((question, index) => (
                        <Form.Group className="form-group" id={`q-${question.questionId}`}>
                            <Form.Label className="question-label">{index + 1}. {question.question}</Form.Label>
                            <ul>
                                {question.options.map((option, idx) => (
                                    <Form.Check type={`${'MC_MULTI_SELECT' === question.type ? 'checkbox' : 'radio'}`}
                                        label={`${option.optionValue}`}
                                        name={`group-${question.questionId}`}
                                        id={`ans-${option.optionId}`}
                                        onChange={this.handleChange} />
                                ))}
                            </ul>
                        </Form.Group>
                    ))}
                    <Button variant="primary" type="submit" disabled={!complete}>
                        Submit
                    </Button>
                </Form>
            </Container>
        )
    };
}

export default withNavigationBlocker(Quiz);
